import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '../services/api';
import {
  BarChart, Bar, AreaChart, Area, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';
import {
  ChartBarIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentTextIcon,
  CpuChipIcon,
  ArrowTrendingUpIcon,
  ExclamationTriangleIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  BoltIcon,
  ShieldCheckIcon,
  UsersIcon,
  FireIcon,
  StarIcon,
  EyeIcon,
  MoonIcon,
  ClockIcon,
  DocumentDuplicateIcon,
  BeakerIcon,
} from '@heroicons/react/24/outline';

// ─── Pricing ──────────────────────────────────────────────────────────────────
const PRICE_INPUT_PER_1M  = 3.00;
const PRICE_OUTPUT_PER_1M = 15.00;

const calcCost = (inputTokens, outputTokens) =>
  (Number(inputTokens)  / 1_000_000) * PRICE_INPUT_PER_1M +
  (Number(outputTokens) / 1_000_000) * PRICE_OUTPUT_PER_1M;

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt         = (n, d = 0) => n == null ? '—' : Number(n).toLocaleString(undefined, { maximumFractionDigits: d });
const fmtUSD      = (n) => n == null ? '—' : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`;
const fmtUSDShort = (n) => {
  if (n == null) return '—';
  if (n >= 1000) return `$${(n / 1000).toFixed(2)}k`;
  if (n >= 1)    return `$${n.toFixed(3)}`;
  return `$${n.toFixed(5)}`;
};

const parseDurationSecs = (dur) => {
  if (!dur) return 0;
  const m = dur.match(/(\d+):(\d+):(\d+)\.?(\d*)/);
  if (!m) return 0;
  return parseInt(m[1]) * 3600 + parseInt(m[2]) * 60 + parseInt(m[3]);
};

const getWeek = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  const day = d.getDay() || 7;
  const monday = new Date(d);
  monday.setDate(d.getDate() - day + 1);
  return monday.toISOString().slice(0, 10);
};

const getMonth = (dateStr) => {
  if (!dateStr) return 'Unknown';
  const d = new Date(dateStr);
  return isNaN(d) ? 'Unknown' : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const normalizeStatus = (s) => {
  if (!s) return '';
  const v = String(s).toLowerCase();
  if (v.includes('completed') || v.includes('success')) return 'completed';
  if (v.includes('failed'))   return 'failed';
  if (v.includes('cancelled') || v.includes('canceled')) return 'cancelled';
  if (v.includes('processing')) return 'processing';
  if (v.includes('queued'))   return 'queued';
  return v;
};

// ─── Identify single analysis jobs ────────────────────────────────────────────
const isSingleAnalysis = (job) => job.req_document_name === 'single_requirement';

// ─── UI Primitives ────────────────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, sub, accent = 'primary', delay = 0, iconBg }) {
  const accents = {
    primary:   'from-primary-500 to-primary-600',
    secondary: 'from-secondary-500 to-secondary-600',
    success:   'from-emerald-500 to-emerald-600',
    warning:   'from-amber-500 to-amber-600',
    info:      'from-sky-500 to-sky-600',
    rose:      'from-rose-500 to-rose-600',
    violet:    'from-violet-500 to-violet-600',
    teal:      'from-teal-500 to-teal-600',
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.3 }}
      className="card p-5 flex items-start gap-4"
    >
      <div className={`flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br ${iconBg || accents[accent]} flex items-center justify-center shadow-lg`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-0.5">{label}</p>
        <p className="text-2xl font-bold text-neutral-900 dark:text-white leading-none truncate">{value}</p>
        {sub && <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">{sub}</p>}
      </div>
    </motion.div>
  );
}

function SectionHeader({ icon: Icon, title, subtitle, color = 'text-primary-600 dark:text-primary-400' }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <Icon className={`w-6 h-6 flex-shrink-0 ${color}`} />
      <div>
        <h2 className="text-lg font-bold text-neutral-900 dark:text-white">{title}</h2>
        {subtitle && <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">{subtitle}</p>}
      </div>
    </div>
  );
}

function MiniBar({ pct, color = 'bg-primary-500', height = 'h-1.5' }) {
  return (
    <div className={`w-full ${height} rounded-full bg-neutral-200 dark:bg-neutral-700 overflow-hidden`}>
      <motion.div
        className={`h-full rounded-full ${color}`}
        initial={{ width: 0 }}
        animate={{ width: `${Math.min(100, Math.max(0, pct || 0))}%` }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      />
    </div>
  );
}

// ─── Shared chart config ──────────────────────────────────────────────────────
const CHART_COLORS = {
  sky:     '#38bdf8',
  indigo:  '#6366f1',
  violet:  '#8b5cf6',
  amber:   '#f59e0b',
  orange:  '#f97316',
  emerald: '#10b981',
  rose:    '#f43f5e',
  primary: '#3b82f6',
  teal:    '#14b8a6',
};

const tooltipStyle = {
  contentStyle: {
    background: '#2d3f55',
    border: '1px solid #4a5f78',
    borderRadius: 8,
    fontSize: 12,
    color: '#f1f5f9',
    boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
  },
  labelStyle:  { color: '#94a3b8', marginBottom: 6, fontWeight: 600 },
  itemStyle:   { color: '#f1f5f9', padding: '1px 0' },
  cursor:      { fill: 'rgba(99,102,241,0.08)' },
};

const axisStyle = { fill: '#94a3b8', fontSize: 11 };

const getISOWeekNum = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d)) return '';
  const jan4 = new Date(d.getFullYear(), 0, 4);
  const startOfWeek1 = new Date(jan4);
  startOfWeek1.setDate(jan4.getDate() - ((jan4.getDay() + 6) % 7));
  return Math.floor(((d - startOfWeek1) / 86400000) / 7) + 1;
};

const fmtWeekLabel = (w) => {
  if (!w) return '';
  const d = new Date(w);
  if (isNaN(d)) return w;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const fmtWeekFull = (w) => {
  if (!w) return '';
  const d = new Date(w);
  if (isNaN(d)) return w;
  return `W${getISOWeekNum(w)} — ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
};

const fmtMonthLabel = (m) => {
  if (!m) return '';
  const [y, mo] = m.split('-');
  const d = new Date(Number(y), Number(mo) - 1, 1);
  return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
};

const fmtMonthFull = (m) => {
  if (!m) return '';
  const [y, mo] = m.split('-');
  const d = new Date(Number(y), Number(mo) - 1, 1);
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

const noData = <p className="text-sm text-neutral-400 text-center py-10">No data available</p>;

// ─── Interactive Recharts components ──────────────────────────────────────────

function MonthlyBarChart({ data, valueKey, color = CHART_COLORS.sky, formatter = fmt, label }) {
  if (!data?.length) return noData;
  const d = data.slice(-9).map(r => ({ ...r, _label: fmtMonthLabel(r.month), _full: fmtMonthFull(r.month) }));
  return (
    <ResponsiveContainer width="100%" height={240}>
      <BarChart data={d} margin={{ top: 8, right: 20, left: 0, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
        <XAxis dataKey="_label" tick={axisStyle} axisLine={false} tickLine={false} />
        <YAxis tick={axisStyle} axisLine={false} tickLine={false} tickFormatter={formatter} width={68} />
        <Tooltip
          contentStyle={tooltipStyle.contentStyle}
          labelStyle={tooltipStyle.labelStyle}
          itemStyle={tooltipStyle.itemStyle}
          cursor={tooltipStyle.cursor}
          formatter={(v) => [formatter(v), label || valueKey]}
          labelFormatter={(_, payload) => payload?.[0]?.payload?._full || ''}
        />
        <Bar dataKey={valueKey} fill={color} radius={[4, 4, 0, 0]} maxBarSize={52} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function WeeklyBarChart({ data, valueKey, color = CHART_COLORS.sky, formatter = fmt, label }) {
  if (!data?.length) return noData;
  const d = data.slice(-14).map(r => ({ ...r, _label: fmtWeekLabel(r.week), _full: fmtWeekFull(r.week) }));
  return (
    <ResponsiveContainer width="100%" height={240}>
      <BarChart data={d} margin={{ top: 8, right: 20, left: 0, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
        <XAxis dataKey="_label" tick={axisStyle} axisLine={false} tickLine={false} interval="preserveStartEnd" />
        <YAxis tick={axisStyle} axisLine={false} tickLine={false} tickFormatter={formatter} width={68} />
        <Tooltip
          contentStyle={tooltipStyle.contentStyle}
          labelStyle={tooltipStyle.labelStyle}
          itemStyle={tooltipStyle.itemStyle}
          cursor={tooltipStyle.cursor}
          formatter={(v) => [formatter(v), label || valueKey]}
          labelFormatter={(_, payload) => payload?.[0]?.payload?._full || ''}
        />
        <Bar dataKey={valueKey} fill={color} radius={[4, 4, 0, 0]} maxBarSize={44} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function AreaLineChart({ data, valueKey, color = CHART_COLORS.indigo, formatter = fmt, label, step = false }) {
  if (!data?.length) return noData;
  const d = data.slice(-14).map(r => ({ ...r, _label: fmtWeekLabel(r.week), _full: fmtWeekFull(r.week) }));
  const gradId = `grad-${valueKey}`;
  return (
    <ResponsiveContainer width="100%" height={240}>
      <AreaChart data={d} margin={{ top: 8, right: 20, left: 0, bottom: 4 }}>
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%"  stopColor={color} stopOpacity={0.35} />
            <stop offset="95%" stopColor={color} stopOpacity={0.04} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
        <XAxis dataKey="_label" tick={axisStyle} axisLine={false} tickLine={false} interval="preserveStartEnd" />
        <YAxis tick={axisStyle} axisLine={false} tickLine={false} tickFormatter={formatter} width={68} />
        <Tooltip
          contentStyle={tooltipStyle.contentStyle}
          labelStyle={tooltipStyle.labelStyle}
          itemStyle={tooltipStyle.itemStyle}
          formatter={(v) => [formatter(v), label || valueKey]}
          labelFormatter={(_, payload) => payload?.[0]?.payload?._full || ''}
        />
        <Area
          type={step ? 'stepAfter' : 'monotone'}
          dataKey={valueKey}
          stroke={color} strokeWidth={2.5}
          fill={`url(#${gradId})`}
          dot={{ r: 3, fill: color, strokeWidth: 0 }}
          activeDot={{ r: 5, strokeWidth: 2, stroke: color, fill: '#2d3f55' }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function StackedTokenChart({ data }) {
  if (!data?.length) return noData;
  const d = data.slice(-14).map(r => ({
    _label: fmtWeekLabel(r.week),
    _full:  fmtWeekFull(r.week),
    'Input Tokens':  r.inputTokens  || 0,
    'Output Tokens': r.outputTokens || 0,
  }));
  return (
    <ResponsiveContainer width="100%" height={260}>
      <AreaChart data={d} margin={{ top: 8, right: 20, left: 0, bottom: 4 }}>
        <defs>
          <linearGradient id="gradTokIn" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%"  stopColor={CHART_COLORS.emerald} stopOpacity={0.5} />
            <stop offset="95%" stopColor={CHART_COLORS.emerald} stopOpacity={0.05} />
          </linearGradient>
          <linearGradient id="gradTokOut" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%"  stopColor={CHART_COLORS.sky} stopOpacity={0.45} />
            <stop offset="95%" stopColor={CHART_COLORS.sky} stopOpacity={0.03} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
        <XAxis dataKey="_label" tick={axisStyle} axisLine={false} tickLine={false} interval="preserveStartEnd" />
        <YAxis tick={axisStyle} axisLine={false} tickLine={false} tickFormatter={fmt} width={72} />
        <Tooltip
          contentStyle={tooltipStyle.contentStyle}
          labelStyle={tooltipStyle.labelStyle}
          itemStyle={tooltipStyle.itemStyle}
          formatter={(v, name) => [fmt(v), name]}
          labelFormatter={(_, payload) => payload?.[0]?.payload?._full || ''}
        />
        <Legend wrapperStyle={{ fontSize: 11, color: '#94a3b8', paddingTop: 8 }} />
        <Area type="monotone" dataKey="Input Tokens"  stackId="1"
          stroke={CHART_COLORS.emerald} fill="url(#gradTokIn)"  strokeWidth={1.5} dot={false} activeDot={{ r: 4 }} />
        <Area type="monotone" dataKey="Output Tokens" stackId="1"
          stroke={CHART_COLORS.sky}     fill="url(#gradTokOut)" strokeWidth={1.5} dot={false} activeDot={{ r: 4 }} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

// ─── NEW: Dual-line chart for comparing batch vs single over time ─────────────
function DualLineChart({ data, keyA, keyB, labelA, labelB, colorA = CHART_COLORS.sky, colorB = CHART_COLORS.teal, formatter = fmt }) {
  if (!data?.length) return noData;
  const d = data.slice(-14).map(r => ({ ...r, _label: fmtWeekLabel(r.week), _full: fmtWeekFull(r.week) }));
  return (
    <ResponsiveContainer width="100%" height={240}>
      <LineChart data={d} margin={{ top: 8, right: 20, left: 0, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
        <XAxis dataKey="_label" tick={axisStyle} axisLine={false} tickLine={false} interval="preserveStartEnd" />
        <YAxis tick={axisStyle} axisLine={false} tickLine={false} tickFormatter={formatter} width={52} />
        <Tooltip
          contentStyle={tooltipStyle.contentStyle}
          labelStyle={tooltipStyle.labelStyle}
          itemStyle={tooltipStyle.itemStyle}
          formatter={(v, name) => [formatter(v), name]}
          labelFormatter={(_, payload) => payload?.[0]?.payload?._full || ''}
        />
        <Legend wrapperStyle={{ fontSize: 11, color: '#94a3b8', paddingTop: 8 }} />
        <Line type="monotone" dataKey={keyA} name={labelA} stroke={colorA} strokeWidth={2.5}
          dot={{ r: 3, fill: colorA, strokeWidth: 0 }} activeDot={{ r: 5 }} />
        <Line type="monotone" dataKey={keyB} name={labelB} stroke={colorB} strokeWidth={2.5}
          dot={{ r: 3, fill: colorB, strokeWidth: 0 }} activeDot={{ r: 5 }} strokeDasharray="5 3" />
      </LineChart>
    </ResponsiveContainer>
  );
}

function DonutChart({ segments }) {
  const COLORS = [CHART_COLORS.violet, CHART_COLORS.sky, CHART_COLORS.amber, '#94a3b8'];
  const total = segments.reduce((s, seg) => s + (seg.count || 0), 0);
  if (!total) return noData;
  const d = segments.map((s, i) => ({ name: s.label, value: s.count, color: COLORS[i] }));
  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const p = payload[0];
    return (
      <div style={{ ...tooltipStyle.contentStyle, padding: '8px 12px' }}>
        <p style={{ color: p.payload.color, fontWeight: 700, marginBottom: 2 }}>{p.name}</p>
        <p style={{ color: '#f1f5f9' }}>{p.value} users ({Math.round((p.value / total) * 100)}%)</p>
      </div>
    );
  };
  const renderLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    if (percent < 0.06) return null;
    const RADIAN = Math.PI / 180;
    const r = innerRadius + (outerRadius - innerRadius) * 0.55;
    const x = cx + r * Math.cos(-midAngle * RADIAN);
    const y = cy + r * Math.sin(-midAngle * RADIAN);
    return <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={700}>{`${Math.round(percent * 100)}%`}</text>;
  };
  return (
    <div className="flex items-center gap-6">
      <ResponsiveContainer width={210} height={210}>
        <PieChart>
          <Pie data={d} cx="50%" cy="50%" innerRadius={55} outerRadius={92}
            paddingAngle={2} dataKey="value" stroke="none" labelLine={false} label={renderLabel}>
            {d.map((entry, i) => <Cell key={i} fill={entry.color} />)}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
        </PieChart>
      </ResponsiveContainer>
      <div className="space-y-3 flex-1">
        {d.map((s, i) => (
          <div key={i} className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />
              <span className="text-sm text-neutral-600 dark:text-neutral-300">{s.name}</span>
            </div>
            <span className="text-sm font-semibold text-neutral-800 dark:text-neutral-100">
              {s.value} <span className="text-xs text-neutral-400 font-normal">({Math.round((s.value / total) * 100)}%)</span>
            </span>
          </div>
        ))}
        <p className="text-xs text-neutral-400 pt-2 border-t border-neutral-100 dark:border-neutral-700">
          Total: <span className="font-semibold text-neutral-600 dark:text-neutral-300">{total} users</span>
        </p>
      </div>
    </div>
  );
}

function DurationHistogram({ buckets }) {
  if (!buckets?.length) return noData;
  const d = buckets.map(b => ({ label: b.label, Jobs: b.count }));
  return (
    <ResponsiveContainer width="100%" height={240}>
      <BarChart data={d} margin={{ top: 8, right: 20, left: 0, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
        <XAxis dataKey="label" tick={axisStyle} axisLine={false} tickLine={false} />
        <YAxis tick={axisStyle} axisLine={false} tickLine={false} allowDecimals={false} width={40} />
        <Tooltip
          contentStyle={tooltipStyle.contentStyle}
          labelStyle={tooltipStyle.labelStyle}
          itemStyle={tooltipStyle.itemStyle}
          cursor={tooltipStyle.cursor}
          formatter={(v) => [v, 'Jobs']}
        />
        <Bar dataKey="Jobs" fill={CHART_COLORS.violet} radius={[4, 4, 0, 0]} maxBarSize={60} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function DailyUsersChart({ data, userKey = 'uniqueUsers', color = CHART_COLORS.sky, gradId = 'gradDailyUsers' }) {
  if (!data?.length) return noData;
  const fmtDay = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return isNaN(d) ? dateStr : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  const fmtDayFull = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return isNaN(d) ? dateStr : d.toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' });
  };
  const chartData = data.map(r => ({
    ...r,
    _label: fmtDay(r.date),
    _full:  fmtDayFull(r.date),
  }));
  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const p = payload[0]?.payload;
    return (
      <div style={{ ...tooltipStyle.contentStyle, padding: '10px 14px' }}>
        <p style={{ color: '#94a3b8', fontSize: 11, marginBottom: 6, fontWeight: 600 }}>{p?._full}</p>
        <p style={{ color, fontWeight: 700, fontSize: 14 }}>
          {payload[0]?.value} <span style={{ color: '#94a3b8', fontSize: 11, fontWeight: 400 }}>unique users</span>
        </p>
      </div>
    );
  };
  return (
    <ResponsiveContainer width="100%" height={220}>
      <AreaChart data={chartData} margin={{ top: 8, right: 20, left: 0, bottom: 4 }}>
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%"  stopColor={color} stopOpacity={0.4} />
            <stop offset="95%" stopColor={color} stopOpacity={0.03} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
        <XAxis
          dataKey="_label"
          tick={axisStyle}
          axisLine={false}
          tickLine={false}
          interval={Math.max(0, Math.floor(chartData.length / 10))}
        />
        <YAxis
          tick={axisStyle}
          axisLine={false}
          tickLine={false}
          allowDecimals={false}
          width={36}
        />
        <Tooltip content={<CustomTooltip />} />
        <Area
          type="monotone"
          dataKey={userKey}
          stroke={color}
          strokeWidth={2}
          fill={`url(#${gradId})`}
          dot={false}
          activeDot={{ r: 5, fill: color, strokeWidth: 2, stroke: '#fff' }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

// ─── NEW: Daily chart that supports two series (batch vs single) ──────────────
function DailyDualChart({ data, keyA, keyB, labelA, labelB, colorA = CHART_COLORS.sky, colorB = CHART_COLORS.teal }) {
  if (!data?.length) return noData;
  const fmtDay = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return isNaN(d) ? dateStr : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  const fmtDayFull = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return isNaN(d) ? dateStr : d.toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' });
  };
  const chartData = data.map(r => ({ ...r, _label: fmtDay(r.date), _full: fmtDayFull(r.date) }));
  return (
    <ResponsiveContainer width="100%" height={260}>
      <AreaChart data={chartData} margin={{ top: 8, right: 20, left: 0, bottom: 4 }}>
        <defs>
          <linearGradient id="gradDualA" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%"  stopColor={colorA} stopOpacity={0.35} />
            <stop offset="95%" stopColor={colorA} stopOpacity={0.03} />
          </linearGradient>
          <linearGradient id="gradDualB" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%"  stopColor={colorB} stopOpacity={0.35} />
            <stop offset="95%" stopColor={colorB} stopOpacity={0.03} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
        <XAxis dataKey="_label" tick={axisStyle} axisLine={false} tickLine={false}
          interval={Math.max(0, Math.floor(chartData.length / 10))} />
        <YAxis tick={axisStyle} axisLine={false} tickLine={false} allowDecimals={false} width={36} />
        <Tooltip
          contentStyle={tooltipStyle.contentStyle}
          labelStyle={tooltipStyle.labelStyle}
          itemStyle={tooltipStyle.itemStyle}
          formatter={(v, name) => [v, name]}
          labelFormatter={(_, payload) => payload?.[0]?.payload?._full || ''}
        />
        <Legend wrapperStyle={{ fontSize: 11, color: '#94a3b8', paddingTop: 8 }} />
        <Area type="monotone" dataKey={keyA} name={labelA} stroke={colorA} strokeWidth={2}
          fill="url(#gradDualA)" dot={false} activeDot={{ r: 4 }} />
        <Area type="monotone" dataKey={keyB} name={labelB} stroke={colorB} strokeWidth={2}
          fill="url(#gradDualB)" dot={false} activeDot={{ r: 4 }} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function DocumentRankChart({ docs, color = CHART_COLORS.amber, valueKey = 'jobs', formatter = fmt, label }) {
  if (!docs?.length) return noData;
  const d = [...docs].slice(0, 10).map(doc => ({
    name:       doc.name.length > 28 ? doc.name.slice(0, 26) + '…' : doc.name,
    fullName:   doc.name,
    [valueKey]: doc[valueKey],
  }));
  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    return (
      <div style={{ ...tooltipStyle.contentStyle, padding: '8px 12px', maxWidth: 300 }}>
        <p style={{ color: '#94a3b8', fontSize: 11, marginBottom: 4, wordBreak: 'break-word' }}>{payload[0]?.payload?.fullName}</p>
        <p style={{ color: '#f1f5f9', fontWeight: 700 }}>{formatter(payload[0]?.value)} {label || valueKey}</p>
      </div>
    );
  };
  return (
    <ResponsiveContainer width="100%" height={Math.max(220, d.length * 36 + 24)}>
      <BarChart data={d} layout="vertical" margin={{ top: 4, right: 72, left: 8, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
        <XAxis type="number" tick={axisStyle} axisLine={false} tickLine={false} tickFormatter={formatter} />
        <YAxis type="category" dataKey="name" tick={{ ...axisStyle, fontSize: 11 }} axisLine={false} tickLine={false} width={148} />
        <Tooltip content={<CustomTooltip />} />
        <Bar dataKey={valueKey} fill={color} radius={[0, 4, 4, 0]} maxBarSize={22}
          label={{ position: 'right', fontSize: 10, fill: '#94a3b8', formatter }} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function UserSegmentBadge({ segment }) {
  const meta = {
    'Power User':      { cls: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300', Icon: FireIcon },
    'Regular User':    { cls: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300', Icon: StarIcon },
    'Occasional User': { cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300', Icon: EyeIcon },
    'Dormant User':    { cls: 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400', Icon: MoonIcon },
  };
  const m = meta[segment] || meta['Occasional User'];
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${m.cls}`}>
      <m.Icon className="w-3 h-3" />{segment}
    </span>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function UserObservability({ userId }) {
  const [jobs, setJobs]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [lastRefresh, setLastRefresh] = useState(null);
  const [activeSection, setActiveSection] = useState('adoption');

  const fetchData = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res  = await api.getUserObservability();
      const data = Array.isArray(res.data) ? res.data : (res.data?.items ?? []);
      setJobs(data);
      setLastRefresh(new Date());
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.message || err.message || 'Failed to load';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const a = useMemo(() => {
    if (!jobs.length) return null;

    // ── Split batch vs single ──────────────────────────────────────────────
    const batchJobs  = jobs.filter(j => !isSingleAnalysis(j));
    const singleJobs = jobs.filter(j =>  isSingleAnalysis(j));

    const completed = jobs.filter(j => normalizeStatus(j.execution_status) === 'completed');
    const failed    = jobs.filter(j => normalizeStatus(j.execution_status) === 'failed');

    // Per-user map — only for segment counts, IDs never surfaced in charts
    const userMap = {};
    jobs.forEach(j => {
      const uid = j.user_id || 'Unknown';
      if (!userMap[uid]) userMap[uid] = { jobs: 0, completedJobs: 0 };
      userMap[uid].jobs++;
      if (normalizeStatus(j.execution_status) === 'completed') userMap[uid].completedJobs++;
    });
    const maxJobs = Math.max(...Object.values(userMap).map(u => u.jobs), 1);
    const users = Object.values(userMap).map(u => ({
      ...u,
      segment: (() => {
        const r = u.jobs / maxJobs;
        return r >= 0.5 ? 'Power User' : r >= 0.2 ? 'Regular User' : r >= 0.05 ? 'Occasional User' : 'Dormant User';
      })(),
    }));

    // Monthly aggregation
    const monthMap = {};
    jobs.forEach(j => {
      const m = getMonth(j.start_date_time);
      if (!monthMap[m]) monthMap[m] = { month: m, jobs: 0, uniqueUsers: new Set(), tokens: 0, cost: 0, requirements: 0, failed: 0, llmCalls: 0 };
      monthMap[m].jobs++;
      monthMap[m].uniqueUsers.add(j.user_id);
      monthMap[m].tokens       += (Number(j.total_input_tokens) || 0) + (Number(j.total_output_tokens) || 0);
      monthMap[m].cost         += calcCost(j.total_input_tokens, j.total_output_tokens);
      monthMap[m].requirements += Number(j.total_requirements_processed) || 0;
      monthMap[m].llmCalls     += Number(j.total_llm_calls) || 0;
      if (normalizeStatus(j.execution_status) === 'failed') monthMap[m].failed++;
    });
    const monthly = Object.values(monthMap)
      .filter(m => m.month !== 'Unknown')
      .sort((a, b) => a.month.localeCompare(b.month))
      .map(m => ({ ...m, uniqueUsers: m.uniqueUsers.size }));

    // Weekly aggregation
    const weekMap = {};
    jobs.forEach(j => {
      const w = getWeek(j.start_date_time);
      if (!w) return;
      if (!weekMap[w]) weekMap[w] = {
        week: w, jobs: 0, uniqueUsers: new Set(), inputTokens: 0, outputTokens: 0,
        llmCalls: 0, requirements: 0, failed: 0, uniqueDocs: new Set(),
        singleJobs: 0, singleUsers: new Set(), singleTokens: 0,
      };
      weekMap[w].jobs++;
      weekMap[w].uniqueUsers.add(j.user_id);
      weekMap[w].inputTokens  += Number(j.total_input_tokens)  || 0;
      weekMap[w].outputTokens += Number(j.total_output_tokens) || 0;
      weekMap[w].llmCalls     += Number(j.total_llm_calls)     || 0;
      weekMap[w].requirements += Number(j.total_requirements_processed) || 0;
      if (j.req_document_name) weekMap[w].uniqueDocs.add(j.req_document_name);
      if (normalizeStatus(j.execution_status) === 'failed') weekMap[w].failed++;
      // single analysis counts
      if (isSingleAnalysis(j)) {
        weekMap[w].singleJobs++;
        weekMap[w].singleUsers.add(j.user_id);
        weekMap[w].singleTokens += (Number(j.total_input_tokens) || 0) + (Number(j.total_output_tokens) || 0);
      }
    });

    // Build weekly array with cumulative fields
    let cumLLM = 0, cumIn = 0, cumOut = 0;
    const weekly = Object.values(weekMap)
      .sort((a, b) => a.week.localeCompare(b.week))
      .map(w => {
        cumLLM += w.llmCalls;
        cumIn  += w.inputTokens;
        cumOut += w.outputTokens;
        return {
          ...w,
          uniqueUsers:  w.uniqueUsers.size,
          uniqueDocs:   w.uniqueDocs.size,
          singleUsers:  w.singleUsers.size,
          batchJobs:    w.jobs - w.singleJobs,
          cumLLMCalls:  cumLLM,
          cumInputTokens:  cumIn,
          cumOutputTokens: cumOut,
        };
      });

    // Cumulative unique docs per week (global across all time)
    const seenDocs = new Set();
    const jobsSorted = [...jobs].sort((a, b) => new Date(a.start_date_time) - new Date(b.start_date_time));
    const weekDocsCum = {};
    jobsSorted.forEach(j => {
      const w = getWeek(j.start_date_time);
      if (!w) return;
      if (j.req_document_name) seenDocs.add(j.req_document_name);
      weekDocsCum[w] = seenDocs.size;
    });
    weekly.forEach(w => { w.cumUniqueDocs = weekDocsCum[w.week] || 0; });

    // Document-level stats
    const docMap = {};
    jobs.forEach(j => {
      const name = j.req_document_name || 'Unknown';
      if (!docMap[name]) docMap[name] = { name, jobs: 0, requirements: 0, cost: 0, failed: 0 };
      docMap[name].jobs++;
      docMap[name].requirements += Number(j.total_requirements_processed) || 0;
      docMap[name].cost         += calcCost(j.total_input_tokens, j.total_output_tokens);
      if (normalizeStatus(j.execution_status) === 'failed') docMap[name].failed++;
    });
    const docList = Object.values(docMap).sort((a, b) => b.jobs - a.jobs);

    // Daily unique users — all jobs
    const dayMap = {};
    jobs.forEach(j => {
      if (!j.start_date_time) return;
      const d = new Date(j.start_date_time);
      if (isNaN(d)) return;
      const day = d.toISOString().slice(0, 10);
      if (!dayMap[day]) dayMap[day] = { date: day, users: new Set(), singleUsers: new Set(), batchUsers: new Set(), singleCount: 0, batchCount: 0 };
      dayMap[day].users.add(j.user_id);
      if (isSingleAnalysis(j)) {
        dayMap[day].singleUsers.add(j.user_id);
        dayMap[day].singleCount++;
      } else {
        dayMap[day].batchUsers.add(j.user_id);
        dayMap[day].batchCount++;
      }
    });
    const daily = Object.values(dayMap)
      .sort((a, b) => a.date.localeCompare(b.date))
      .map(d => ({
        date:         d.date,
        uniqueUsers:  d.users.size,
        singleUsers:  d.singleUsers.size,
        batchUsers:   d.batchUsers.size,
        singleCount:  d.singleCount,
        batchCount:   d.batchCount,
      }));

    // Duration buckets
    const bucketDefs = [
      { label: '0–5 min',   min: 0,    max: 300  },
      { label: '5–15 min',  min: 300,  max: 900  },
      { label: '15–30 min', min: 900,  max: 1800 },
      { label: '30–60 min', min: 1800, max: 3600 },
      { label: '60+ min',   min: 3600, max: Infinity },
    ];
    const buckets = bucketDefs.map(b => ({
      ...b,
      count: jobs.filter(j => { const s = parseDurationSecs(j.duration); return s >= b.min && s < b.max; }).length,
    }));

    const durSecs = jobs.map(j => parseDurationSecs(j.duration)).filter(s => s > 0);
    const avgDurSecs = durSecs.length ? durSecs.reduce((a, b) => a + b, 0) / durSecs.length : 0;
    const sorted = [...durSecs].sort((a, b) => a - b);
    const medDurSecs = sorted.length ? sorted[Math.floor(sorted.length / 2)] : 0;

    const growthRate = monthly.length >= 2
      ? (monthly[monthly.length - 2].jobs > 0
          ? Math.round(((monthly[monthly.length - 1].jobs - monthly[monthly.length - 2].jobs) / monthly[monthly.length - 2].jobs) * 100)
          : null)
      : null;

    const totalInputTokens  = jobs.reduce((s, j) => s + (Number(j.total_input_tokens)  || 0), 0);
    const totalOutputTokens = jobs.reduce((s, j) => s + (Number(j.total_output_tokens) || 0), 0);
    const totalCost         = calcCost(totalInputTokens, totalOutputTokens);
    const totalRequirements = jobs.reduce((s, j) => s + (Number(j.total_requirements_processed) || 0), 0);
    const totalDurationSecs = jobs.reduce((s, j) => s + parseDurationSecs(j.duration), 0);
    const totalLLMCalls     = jobs.reduce((s, j) => s + (Number(j.total_llm_calls) || 0), 0);
    const uniqueUsers       = Object.keys(userMap).length;
    const uniqueDocs        = new Set(jobs.map(j => j.req_document_name).filter(Boolean)).size;

    // ── Single analysis aggregates ─────────────────────────────────────────
    const singleTotalJobs     = singleJobs.length;
    const singleUniqueUsers   = new Set(singleJobs.map(j => j.user_id).filter(Boolean)).size;
    const singleInputTokens   = singleJobs.reduce((s, j) => s + (Number(j.total_input_tokens)  || 0), 0);
    const singleOutputTokens  = singleJobs.reduce((s, j) => s + (Number(j.total_output_tokens) || 0), 0);
    const singleCost          = calcCost(singleInputTokens, singleOutputTokens);
    const singleLLMCalls      = singleJobs.reduce((s, j) => s + (Number(j.total_llm_calls)     || 0), 0);
    const singleDurSecs       = singleJobs.map(j => parseDurationSecs(j.duration)).filter(s => s > 0);
    const singleAvgDurSecs    = singleDurSecs.length ? singleDurSecs.reduce((a, b) => a + b, 0) / singleDurSecs.length : 0;
    const singleSuccessRate   = singleTotalJobs > 0
      ? Math.round((singleJobs.filter(j => normalizeStatus(j.execution_status) === 'completed').length / singleTotalJobs) * 100)
      : 0;

    // Rules file usage for single analysis
    const singleRulesMap = {};
    singleJobs.forEach(j => {
      const name = j.rules_document_name || 'Unknown';
      if (!singleRulesMap[name]) singleRulesMap[name] = { name, count: 0, cost: 0 };
      singleRulesMap[name].count++;
      singleRulesMap[name].cost += calcCost(j.total_input_tokens, j.total_output_tokens);
    });
    const singleRulesList = Object.values(singleRulesMap).sort((a, b) => b.count - a.count);

    // Single: monthly usage
    const singleMonthMap = {};
    singleJobs.forEach(j => {
      const m = getMonth(j.start_date_time);
      if (!singleMonthMap[m]) singleMonthMap[m] = { month: m, jobs: 0, uniqueUsers: new Set() };
      singleMonthMap[m].jobs++;
      singleMonthMap[m].uniqueUsers.add(j.user_id);
    });
    const singleMonthly = Object.values(singleMonthMap)
      .filter(m => m.month !== 'Unknown')
      .sort((a, b) => a.month.localeCompare(b.month))
      .map(m => ({ ...m, uniqueUsers: m.uniqueUsers.size }));

    return {
      totalJobs: jobs.length, uniqueUsers, uniqueDocs,
      completed, failed,
      successRate: Math.round((completed.length / jobs.length) * 100),
      growthRate, monthly, weekly, daily, users, docList, buckets,
      totalInputTokens, totalOutputTokens, totalCost,
      totalRequirements, totalDurationSecs, totalLLMCalls,
      avgDurationMins:    (avgDurSecs / 60).toFixed(1),
      medianDurationMins: (medDurSecs / 60).toFixed(1),
      avgJobsPerUser:  uniqueUsers > 0 ? (jobs.length / uniqueUsers).toFixed(1) : 0,
      avgCostPerJob:   jobs.length > 0 ? totalCost / jobs.length : 0,
      avgCostPerUser:  uniqueUsers > 0 ? totalCost / uniqueUsers : 0,
      avgCostPerReq:   totalRequirements > 0 ? totalCost / totalRequirements : 0,
      reqPerHour:      totalDurationSecs > 0 ? (totalRequirements / (totalDurationSecs / 3600)).toFixed(1) : 0,
      avgMinsPerJob:   jobs.length > 0 && totalDurationSecs > 0 ? (totalDurationSecs / 60 / jobs.length).toFixed(1) : 0,
      avgReqPerJob:    jobs.length > 0 ? (totalRequirements / jobs.length).toFixed(1) : 0,
      powerUsers:   users.filter(u => u.segment === 'Power User').length,
      regularUsers: users.filter(u => u.segment === 'Regular User').length,
      occasional:   users.filter(u => u.segment === 'Occasional User').length,
      dormant:      users.filter(u => u.segment === 'Dormant User').length,
      // single analysis
      singleTotalJobs, singleUniqueUsers, singleInputTokens, singleOutputTokens,
      singleCost, singleLLMCalls, singleAvgDurSecs, singleSuccessRate,
      singleRulesList, singleMonthly,
      batchTotalJobs: batchJobs.length,
      avgSingleDurSecs: singleAvgDurSecs,
      avgSingleCost: singleTotalJobs > 0 ? singleCost / singleTotalJobs : 0,
      avgSingleLLMCalls: singleTotalJobs > 0 ? (singleLLMCalls / singleTotalJobs).toFixed(0) : 0,
      singleSharePct: jobs.length > 0 ? Math.round((singleTotalJobs / jobs.length) * 100) : 0,
    };
  }, [jobs]);

  const sections = [
    { id: 'adoption',     label: '1. Adoption & Usage',    icon: UsersIcon,             color: 'text-sky-600 dark:text-sky-400' },
    { id: 'documents',    label: '2. Documents',           icon: DocumentDuplicateIcon, color: 'text-amber-600 dark:text-amber-400' },
    { id: 'cost',         label: '3. Cost & Tokens',       icon: CurrencyDollarIcon,    color: 'text-emerald-600 dark:text-emerald-400' },
    { id: 'value',        label: '4. Value Delivery',      icon: BoltIcon,              color: 'text-violet-600 dark:text-violet-400' },
    { id: 'performance',  label: '5. Performance & Health', icon: ClockIcon,            color: 'text-rose-600 dark:text-rose-400' },
    { id: 'segmentation', label: '6. User Segments',       icon: UserGroupIcon,         color: 'text-violet-600 dark:text-violet-400' },
    { id: 'single',       label: '7. Single Analysis',     icon: BeakerIcon,            color: 'text-teal-600 dark:text-teal-400' },
  ];

  return (
    <div className="space-y-6 w-full max-w-[1400px] mx-auto">

      {/* Page Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white flex items-center gap-2">
            <EyeIcon className="w-6 h-6 text-primary-500" />
            User Observability
            {a && (
              <span className={`ml-2 inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${
                a.successRate >= 90 ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' :
                a.successRate >= 70 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400'
              }`}>
                <ShieldCheckIcon className="w-3.5 h-3.5" />
                {a.successRate}% success
              </span>
            )}
          </h1>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1 font-mono">
            claude-sonnet · ${PRICE_INPUT_PER_1M}/1M in · ${PRICE_OUTPUT_PER_1M}/1M out
            {lastRefresh && <span className="ml-3 font-sans">· Refreshed {lastRefresh.toLocaleTimeString()}</span>}
          </p>
        </div>
        <motion.button
          whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
          onClick={fetchData} disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 text-white rounded-lg shadow-lg shadow-primary-500/25 disabled:opacity-50 text-sm font-medium"
        >
          <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </motion.button>
      </div>

      <AnimatePresence>
        {error && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="flex items-start gap-3 p-4 rounded-xl bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-400"
          >
            <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-sm">Failed to load metrics</p>
              <p className="text-xs mt-0.5">{error}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {loading && (
        <div className="flex items-center justify-center py-24 gap-3 text-neutral-400">
          <ArrowPathIcon className="w-5 h-5 animate-spin" />
          <span className="text-sm">Loading observability data…</span>
        </div>
      )}

      {!loading && a && (
        <>
          {/* Section Nav */}
          <div className="flex flex-wrap gap-2">
            {sections.map(s => (
              <button key={s.id} onClick={() => setActiveSection(s.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  activeSection === s.id
                    ? 'bg-primary-600 text-white shadow-lg shadow-primary-500/25'
                    : 'bg-white dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 border border-neutral-200 dark:border-neutral-700 hover:border-primary-300 dark:hover:border-primary-600'
                }`}
              >
                <s.icon className="w-4 h-4" />{s.label}
              </button>
            ))}
          </div>

          {/* ─────────────────── 1. ADOPTION & USAGE ─────────────────── */}
          {activeSection === 'adoption' && (
            <motion.div key="adoption" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
              <SectionHeader icon={UsersIcon} title="Adoption & Usage" subtitle="Is the tool being adopted and growing?" color="text-sky-600 dark:text-sky-400" />

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard icon={DocumentTextIcon}   label="Total Jobs Executed" value={fmt(a.totalJobs)}    accent="info"      delay={0}    />
                <StatCard icon={UsersIcon}           label="Unique Active Users" value={fmt(a.uniqueUsers)}  accent="primary"   delay={0.05} />
                <StatCard icon={ChartBarIcon}        label="Avg Jobs per User"   value={a.avgJobsPerUser}    accent="secondary" delay={0.1}  />
                <StatCard icon={ArrowTrendingUpIcon} label="Monthly Growth Rate"
                  value={a.growthRate != null ? `${a.growthRate > 0 ? '+' : ''}${a.growthRate}%` : '—'}
                  sub="vs previous month"
                  accent={a.growthRate >= 0 ? 'success' : 'rose'}
                  delay={0.15}
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <div className="card p-5">
                  <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-4">Monthly Job Volume</p>
                  <MonthlyBarChart data={a.monthly} valueKey="jobs" color={CHART_COLORS.sky} />
                </div>
                <div className="card p-5">
                  <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-4">Monthly Active Users</p>
                  <MonthlyBarChart data={a.monthly} valueKey="uniqueUsers" color={CHART_COLORS.primary} label="Users" />
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <div className="card p-5">
                  <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">Daily Unique Users — Full Document</p>
                  <p className="text-xs text-neutral-400 dark:text-neutral-500 mb-4">Batch jobs only · excludes single analysis</p>
                  <DailyUsersChart data={a.daily} userKey="batchUsers" color={CHART_COLORS.sky} gradId="gradDailyBatch" />
                </div>
                <div className="card p-5">
                  <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">Daily Unique Users — Single Analysis</p>
                  <p className="text-xs text-neutral-400 dark:text-neutral-500 mb-4">Ad-hoc single requirement checks only</p>
                  <DailyUsersChart data={a.daily} userKey="singleUsers" color={CHART_COLORS.teal} gradId="gradDailySingle" />
                </div>
              </div>

              <div className="card p-5">
                <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-4">Weekly Job Volume</p>
                <WeeklyBarChart data={a.weekly} valueKey="jobs" color={CHART_COLORS.sky} formatter={n => `${fmt(n)} jobs`} label="Jobs" />
              </div>

            </motion.div>
          )}

          {/* ─────────────────── 2. DOCUMENTS ─────────────────────────── */}
          {activeSection === 'documents' && (
            <motion.div key="documents" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
              <SectionHeader icon={DocumentDuplicateIcon} title="Documents Processed"
                subtitle="Requirements documents parsed and analysed"
                color="text-amber-600 dark:text-amber-400"
              />

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard icon={DocumentDuplicateIcon} label="Unique Documents"      value={fmt(a.uniqueDocs)}         accent="warning"   delay={0}    />
                <StatCard icon={BoltIcon}               label="Total Requirements"    value={fmt(a.totalRequirements)}  accent="info"      delay={0.05} />
                <StatCard icon={DocumentTextIcon}       label="Avg Reqs per Doc (all runs)"
                  value={a.uniqueDocs > 0 ? (a.totalRequirements / a.uniqueDocs).toFixed(1) : '—'}
                  sub="summed across every job run"
                  accent="secondary" delay={0.1}
                />
                <StatCard icon={XCircleIcon}            label="Docs with Failures"
                  value={fmt(a.docList.filter(d => d.failed > 0).length)}
                  sub={`of ${fmt(a.uniqueDocs)} total`}
                  accent="rose" delay={0.15}
                />
              </div>

              <div className="card p-5">
                <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-4">Cumulative Unique Documents Over Time</p>
                <AreaLineChart data={a.weekly} valueKey="cumUniqueDocs" color={CHART_COLORS.amber} formatter={n => `${fmt(n)} docs`} label="Unique Docs" step={true} />
              </div>

              <div className="card p-5">
                <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-4">New Unique Documents per Week</p>
                <WeeklyBarChart data={a.weekly} valueKey="uniqueDocs" color={CHART_COLORS.amber} formatter={n => `${fmt(n)} new`} />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <div className="card p-5">
                  <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-4">Top Documents by Jobs Run</p>
                  <DocumentRankChart docs={a.docList.slice(0, 10)} color={CHART_COLORS.amber} valueKey="jobs" formatter={n => `${fmt(n)} jobs`} />
                </div>
                <div className="card p-5">
                  <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-4">Top Documents by Requirements</p>
                  <DocumentRankChart docs={[...a.docList].sort((x,y)=>y.requirements-x.requirements).slice(0,10)} color={CHART_COLORS.orange} valueKey="requirements" formatter={fmt} />
                </div>
              </div>

              <div className="card overflow-hidden">
                <div className="px-5 py-4 border-b border-neutral-100 dark:border-neutral-700">
                  <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">All Documents</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-neutral-50 dark:bg-neutral-800/60">
                      <tr>{['#','Document Name','Jobs Run','Requirements','Est. Cost','Failures'].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">{h}</th>
                      ))}</tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100 dark:divide-neutral-700/60">
                      {a.docList.map((doc, i) => (
                        <tr key={doc.name} className="hover:bg-neutral-50 dark:hover:bg-neutral-800/40">
                          <td className="px-4 py-3 text-xs text-neutral-400">{i+1}</td>
                          <td className="px-4 py-3 text-xs text-neutral-700 dark:text-neutral-200 max-w-[220px] truncate font-medium" title={doc.name}>{doc.name}</td>
                          <td className="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-200">{fmt(doc.jobs)}</td>
                          <td className="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-200">{fmt(doc.requirements)}</td>
                          <td className="px-4 py-3 text-sm font-medium text-emerald-600 dark:text-emerald-400">{fmtUSDShort(doc.cost)}</td>
                          <td className="px-4 py-3 text-sm font-medium text-rose-600 dark:text-rose-400">{fmt(doc.failed)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </motion.div>
          )}

          {/* ─────────────────── 3. COST & TOKENS ─────────────────────── */}
          {activeSection === 'cost' && (
            <motion.div key="cost" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
              <SectionHeader icon={CurrencyDollarIcon} title="Cost & Token Consumption"
                subtitle={`$${PRICE_INPUT_PER_1M}/1M input · $${PRICE_OUTPUT_PER_1M}/1M output`}
                color="text-emerald-600 dark:text-emerald-400"
              />

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard icon={CurrencyDollarIcon} label="Total Cost"               value={fmtUSDShort(a.totalCost)}                              accent="success"   delay={0}    />
                <StatCard icon={CpuChipIcon}         label="Total Tokens (in + out)"  value={fmt(a.totalInputTokens + a.totalOutputTokens)}          accent="info"      delay={0.05} sub={`${fmt(a.totalInputTokens)} in · ${fmt(a.totalOutputTokens)} out`} />
                <StatCard icon={DocumentTextIcon}   label="Avg Cost per Job"          value={fmtUSD(a.avgCostPerJob)}       accent="secondary" delay={0.1}  />
                <StatCard icon={BoltIcon}           label="Avg Cost per Requirement"  value={fmtUSD(a.avgCostPerReq)}       accent="warning"   delay={0.15} />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <div className="card p-5">
                  <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-4">Token Usage & Cost Split</p>
                  <div className="space-y-4">
                    {[
                      { label: `Input tokens ($${PRICE_INPUT_PER_1M}/1M)`,  val: a.totalInputTokens,  cost: (a.totalInputTokens  / 1_000_000) * PRICE_INPUT_PER_1M,  color: 'bg-emerald-500' },
                      { label: `Output tokens ($${PRICE_OUTPUT_PER_1M}/1M)`,val: a.totalOutputTokens, cost: (a.totalOutputTokens / 1_000_000) * PRICE_OUTPUT_PER_1M, color: 'bg-sky-500'     },
                    ].map(row => (
                      <div key={row.label}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-neutral-600 dark:text-neutral-300">{row.label}</span>
                          <span className="font-semibold text-neutral-900 dark:text-white">{fmt(row.val)}</span>
                        </div>
                        <MiniBar pct={(row.val / (a.totalInputTokens + a.totalOutputTokens + 1)) * 100} color={row.color} />
                        <p className="text-xs text-neutral-400 mt-0.5 text-right">{fmtUSDShort(row.cost)}</p>
                      </div>
                    ))}
                    <div className="pt-2 border-t border-neutral-100 dark:border-neutral-700 space-y-1">
                      <div className="flex justify-between">
                        <span className="text-sm text-neutral-500">Total tokens (input + output)</span>
                        <span className="text-sm font-semibold text-neutral-900 dark:text-white">{fmt(a.totalInputTokens + a.totalOutputTokens)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-neutral-500">Total cost</span>
                        <span className="text-sm font-bold text-neutral-900 dark:text-white">{fmtUSDShort(a.totalCost)}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="card p-5">
                  <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-4">Monthly Cost Trend</p>
                  <MonthlyBarChart data={a.monthly} valueKey="cost" color={CHART_COLORS.emerald} formatter={fmtUSDShort} />
                </div>
              </div>

              <div className="card p-5">
                <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-4">Weekly Token Usage — Input vs Output</p>
                <StackedTokenChart data={a.weekly} />
              </div>

            </motion.div>
          )}

          {/* ─────────────────── 4. VALUE DELIVERY ────────────────────── */}
          {activeSection === 'value' && (
            <motion.div key="value" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
              <SectionHeader icon={BoltIcon} title="Value Delivery"
                subtitle="Business output — how much work the system is completing"
                color="text-violet-600 dark:text-violet-400"
              />

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard icon={BoltIcon}            label="Total Requirements Processed" value={fmt(a.totalRequirements)}  sub={`across ${fmt(a.totalJobs)} jobs`}                           accent="warning"   delay={0}    />
                <StatCard icon={DocumentTextIcon}    label="Avg Requirements per Job"     value={a.avgReqPerJob}             sub="per job run"                                                accent="secondary" delay={0.05} />
                <StatCard icon={ArrowTrendingUpIcon} label="Throughput"                   value={`${a.reqPerHour}/hr`}       sub="requirements per compute-hour"                              accent="info"      delay={0.1}  />
                <StatCard icon={CpuChipIcon}         label="Total LLM Calls"              value={fmt(a.totalLLMCalls)}       sub={`avg ${a.totalJobs > 0 ? (a.totalLLMCalls/a.totalJobs).toFixed(0) : '—'} calls/job`} accent="primary" delay={0.15} />
              </div>

              {/* Business impact callout */}
              <div className="card p-5 bg-gradient-to-r from-violet-50 to-indigo-50 dark:from-violet-900/10 dark:to-indigo-900/10 border border-violet-200 dark:border-violet-800/50">
                <p className="text-xs font-semibold text-violet-700 dark:text-violet-400 uppercase tracking-wide mb-2">Business Impact</p>
                <p className="text-2xl font-bold text-neutral-900 dark:text-white">{fmt(a.totalRequirements)} requirements processed</p>
                <p className="text-sm text-neutral-600 dark:text-neutral-300 mt-1">
                  across <strong>{fmt(a.uniqueDocs)}</strong> unique documents · <strong>{fmt(a.totalJobs)}</strong> jobs · <strong>{fmt(a.uniqueUsers)}</strong> active users · <strong>{a.reqPerHour}</strong> req/hr
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <div className="card p-5">
                  <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-4">Weekly LLM Calls</p>
                  <WeeklyBarChart data={a.weekly} valueKey="llmCalls" color={CHART_COLORS.indigo} formatter={fmt} />
                </div>
                <div className="card p-5">
                  <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-4">Cumulative LLM Calls</p>
                  <AreaLineChart data={a.weekly} valueKey="cumLLMCalls" color="#6366f1" formatter={fmt} />
                </div>
              </div>
            </motion.div>
          )}

          {/* ─────────────────── 5. PERFORMANCE ───────────────────────── */}
          {activeSection === 'performance' && (
            <motion.div key="performance" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
              <SectionHeader icon={ClockIcon} title="Performance & Health" subtitle="Duration statistics, throughput, and system reliability" color="text-rose-600 dark:text-rose-400" />

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard icon={ClockIcon}        label="Avg Duration"      value={`${a.avgDurationMins} min`}    accent="info"      delay={0}    />
                <StatCard icon={ClockIcon}        label="Median Duration"   value={`${a.medianDurationMins} min`} accent="secondary" delay={0.05} />
                <StatCard icon={DocumentTextIcon} label="Avg Reqs per Job"  value={a.avgReqPerJob}                accent="warning"   delay={0.1}  />
                <StatCard icon={DocumentTextIcon} label="Total Compute"
                  value={a.totalDurationSecs >= 3600 ? `${(a.totalDurationSecs/3600).toFixed(1)}h` : `${(a.totalDurationSecs/60).toFixed(0)}m`}
                  sub="wall-clock across all jobs" accent="primary" delay={0.15}
                />
              </div>

              <div className="card p-5">
                <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-4">Duration Distribution (all jobs)</p>
                <DurationHistogram buckets={a.buckets} />
              </div>

              {a.failed.length === 0 ? (
                <div className="card p-4 flex items-center gap-3 bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-200 dark:border-emerald-800/50">
                  <CheckCircleIcon className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  <p className="text-sm text-emerald-700 dark:text-emerald-400 font-medium">
                    100% success rate across all {fmt(a.totalJobs)} jobs — no failures to report.
                  </p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatCard icon={CheckCircleIcon} label="Success Rate"   value={`${a.successRate}%`}     accent="success" delay={0}    />
                    <StatCard icon={XCircleIcon}     label="Failure Rate"   value={`${100-a.successRate}%`} accent="rose"    delay={0.05} />
                    <StatCard icon={CheckCircleIcon} label="Completed Jobs" value={fmt(a.completed.length)} accent="success" delay={0.1}  />
                    <StatCard icon={XCircleIcon}     label="Failed Jobs"    value={fmt(a.failed.length)}    accent="rose"    delay={0.15} />
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    <div className="card p-5">
                      <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-4">Monthly Failed Jobs</p>
                      <MonthlyBarChart data={a.monthly} valueKey="failed" color={CHART_COLORS.rose} />
                    </div>
                    <div className="card p-5">
                      <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-4">Weekly Failed Jobs</p>
                      <WeeklyBarChart data={a.weekly} valueKey="failed" color={CHART_COLORS.rose} formatter={n => `${fmt(n)} failed`} />
                    </div>
                  </div>
                  <div className="card p-5">
                    <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-4">Documents with Most Failures</p>
                    <DocumentRankChart
                      docs={a.docList.filter(d => d.failed > 0).sort((x,y) => y.failed - x.failed).slice(0, 10)}
                      color={CHART_COLORS.rose} valueKey="failed" formatter={n => `${fmt(n)} failed`}
                    />
                  </div>
                </>
              )}
            </motion.div>
          )}

          {/* ─────────────────── 6. USER SEGMENTS ─────────────────────── */}
          {activeSection === 'segmentation' && (
            <motion.div key="segmentation" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
              <SectionHeader icon={UserGroupIcon} title="User Segmentation"
                subtitle="Anonymous usage patterns — no individual identifiers exposed"
                color="text-violet-600 dark:text-violet-400"
              />

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard icon={FireIcon} label="Power Users"      value={fmt(a.powerUsers)}   sub="Top contributors"  iconBg="from-violet-500 to-violet-600" delay={0}    />
                <StatCard icon={StarIcon} label="Regular Users"    value={fmt(a.regularUsers)} sub="Consistent usage"  iconBg="from-sky-500 to-sky-600"       delay={0.05} />
                <StatCard icon={EyeIcon}  label="Occasional Users" value={fmt(a.occasional)}   sub="Infrequent usage"  iconBg="from-amber-500 to-amber-600"   delay={0.1}  />
                <StatCard icon={MoonIcon} label="Dormant Users"    value={fmt(a.dormant)}      sub="Minimal activity"  iconBg="from-neutral-400 to-neutral-500" delay={0.15} />
              </div>

              <div className="card p-5">
                <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-4">Segment Distribution</p>
                <DonutChart segments={[
                  { label: 'Power Users',      count: a.powerUsers   },
                  { label: 'Regular Users',    count: a.regularUsers },
                  { label: 'Occasional Users', count: a.occasional   },
                  { label: 'Dormant Users',    count: a.dormant      },
                ]} />
              </div>

              <div className="card overflow-hidden">
                <div className="px-5 py-4 border-b border-neutral-100 dark:border-neutral-700">
                  <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Segment Summary</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-neutral-50 dark:bg-neutral-800/60">
                      <tr>{['Segment','User Count','% of Total','Definition'].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">{h}</th>
                      ))}</tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100 dark:divide-neutral-700/60">
                      {[
                        { segment: 'Power User',      count: a.powerUsers,   defn: '≥ 50% of top user activity'  },
                        { segment: 'Regular User',    count: a.regularUsers, defn: '20–49% of top user activity' },
                        { segment: 'Occasional User', count: a.occasional,   defn: '5–19% of top user activity'  },
                        { segment: 'Dormant User',    count: a.dormant,      defn: '< 5% of top user activity'   },
                      ].map(row => (
                        <tr key={row.segment} className="hover:bg-neutral-50 dark:hover:bg-neutral-800/40">
                          <td className="px-4 py-3"><UserSegmentBadge segment={row.segment} /></td>
                          <td className="px-4 py-3 text-sm font-semibold text-neutral-700 dark:text-neutral-200">{fmt(row.count)}</td>
                          <td className="px-4 py-3 text-sm text-neutral-600 dark:text-neutral-300">
                            {a.uniqueUsers > 0 ? Math.round((row.count / a.uniqueUsers) * 100) : 0}%
                          </td>
                          <td className="px-4 py-3 text-xs text-neutral-500 dark:text-neutral-400">{row.defn}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </motion.div>
          )}

          {/* ─────────────────── 7. SINGLE ANALYSIS ───────────────────── */}
          {activeSection === 'single' && (
            <motion.div key="single" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
              <SectionHeader icon={BeakerIcon} title="Single Requirement Analysis"
                subtitle="Ad-hoc single requirement checks — identified by req_document_name = 'single_requirement'"
                color="text-teal-600 dark:text-teal-400"
              />

              {a.singleTotalJobs === 0 ? (
                <div className="card p-8 flex flex-col items-center justify-center text-center gap-3">
                  <BeakerIcon className="w-10 h-10 text-neutral-300 dark:text-neutral-600" />
                  <p className="text-sm font-medium text-neutral-600 dark:text-neutral-300">No single analysis jobs recorded yet</p>
                  <p className="text-xs text-neutral-400">Metrics will appear here once users run single requirement checks.</p>
                </div>
              ) : (
                <>
                  {/* KPI row */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatCard icon={BeakerIcon}       label="Single Analyses Run"    value={fmt(a.singleTotalJobs)}    sub={`${a.singleSharePct}% of all jobs`}                                            iconBg="from-teal-500 to-teal-600"    delay={0}    />
                    <StatCard icon={UsersIcon}         label="Unique Users"           value={fmt(a.singleUniqueUsers)}  sub="who used single analysis"                                                      iconBg="from-sky-500 to-sky-600"       delay={0.05} />
                    <StatCard icon={CpuChipIcon}       label="Avg LLM Calls / Check"  value={a.avgSingleLLMCalls}       sub="rules evaluated per run"                                                       iconBg="from-indigo-500 to-indigo-600" delay={0.1}  />
                    <StatCard icon={ClockIcon}         label="Avg Duration"           value={`${(a.avgSingleDurSecs/60).toFixed(1)} min`} sub="per single analysis"                                        iconBg="from-amber-500 to-amber-600"   delay={0.15} />
                  </div>

                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatCard icon={CurrencyDollarIcon} label="Total Cost"           value={fmtUSDShort(a.singleCost)}      sub="single analysis only"                                                     accent="success"   delay={0}    />
                    <StatCard icon={CurrencyDollarIcon} label="Avg Cost / Check"     value={fmtUSD(a.avgSingleCost)}        sub="per single run"                                                           accent="secondary" delay={0.05} />
                    <StatCard icon={CpuChipIcon}         label="Total Tokens"        value={fmt(a.singleInputTokens + a.singleOutputTokens)} sub={`${fmt(a.singleInputTokens)} in · ${fmt(a.singleOutputTokens)} out`} accent="info" delay={0.1} />
                    <StatCard icon={ShieldCheckIcon}     label="Success Rate"        value={`${a.singleSuccessRate}%`}       sub="completed successfully"
                      accent={a.singleSuccessRate >= 90 ? 'success' : a.singleSuccessRate >= 70 ? 'warning' : 'rose'}
                      delay={0.15}
                    />
                  </div>

                  {/* Batch vs Single comparison callout */}
                  <div className="card p-5 bg-gradient-to-r from-teal-50 to-sky-50 dark:from-teal-900/10 dark:to-sky-900/10 border border-teal-200 dark:border-teal-800/50">
                    <p className="text-xs font-semibold text-teal-700 dark:text-teal-400 uppercase tracking-wide mb-3">Batch vs Single — at a glance</p>
                    <div className="grid grid-cols-3 gap-6 text-center">
                      {[
                        { label: 'Batch jobs',   value: fmt(a.batchTotalJobs),   pct: `${100 - a.singleSharePct}%` },
                        { label: 'Single checks', value: fmt(a.singleTotalJobs), pct: `${a.singleSharePct}%`       },
                        { label: 'Total',         value: fmt(a.totalJobs),        pct: '100%'                       },
                      ].map(col => (
                        <div key={col.label}>
                          <p className="text-2xl font-bold text-neutral-900 dark:text-white">{col.value}</p>
                          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">{col.label}</p>
                          <p className="text-xs font-semibold text-teal-600 dark:text-teal-400 mt-1">{col.pct}</p>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4">
                      <MiniBar pct={a.singleSharePct} color="bg-teal-500" height="h-2" />
                      <div className="flex justify-between text-xs text-neutral-400 mt-1">
                        <span>Batch ({100 - a.singleSharePct}%)</span>
                        <span>Single ({a.singleSharePct}%)</span>
                      </div>
                    </div>
                  </div>

                  {/* Charts row 1 */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    <div className="card p-5">
                      <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-4">Monthly Single Analysis Volume</p>
                      <MonthlyBarChart data={a.singleMonthly} valueKey="jobs" color={CHART_COLORS.teal} label="Checks" />
                    </div>
                    <div className="card p-5">
                      <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-4">Monthly Unique Users — Single Analysis</p>
                      <MonthlyBarChart data={a.singleMonthly} valueKey="uniqueUsers" color={CHART_COLORS.sky} label="Users" />
                    </div>
                  </div>

                  {/* Charts row 2 — weekly */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    <div className="card p-5">
                      <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-4">Weekly Single Checks</p>
                      <WeeklyBarChart data={a.weekly} valueKey="singleJobs" color={CHART_COLORS.teal} formatter={n => `${fmt(n)} checks`} label="Checks" />
                    </div>
                    <div className="card p-5">
                      <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-4">Weekly Unique Users — Single Analysis</p>
                      <WeeklyBarChart data={a.weekly} valueKey="singleUsers" color={CHART_COLORS.sky} formatter={n => `${fmt(n)} users`} label="Users" />
                    </div>
                  </div>

                  {/* Daily unique users — combined overlay */}
                  <div className="card p-5">
                    <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">Daily Unique Users — Single vs Full Document</p>
                    <p className="text-xs text-neutral-400 dark:text-neutral-500 mb-4">
                      <span style={{ color: CHART_COLORS.teal }}>■</span> Single analysis &nbsp;·&nbsp;
                      <span style={{ color: CHART_COLORS.sky }}>■</span> Full document (batch)
                    </p>
                    <DailyDualChart
                      data={a.daily}
                      keyA="singleUsers" labelA="Single analysis"
                      keyB="batchUsers"  labelB="Full document"
                      colorA={CHART_COLORS.teal}
                      colorB={CHART_COLORS.sky}
                    />
                  </div>

                  {/* Weekly batch vs single volume overlay */}
                  <div className="card p-5">
                    <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">Weekly Job Mix — Batch vs Single</p>
                    <p className="text-xs text-neutral-400 dark:text-neutral-500 mb-4">How the two modes are trending over time</p>
                    <DualLineChart
                      data={a.weekly}
                      keyA="batchJobs"  labelA="Batch jobs"
                      keyB="singleJobs" labelB="Single checks"
                      colorA={CHART_COLORS.sky}
                      colorB={CHART_COLORS.teal}
                    />
                  </div>

                  {/* Rules file leaderboard */}
                  {a.singleRulesList.length > 0 && (
                    <div className="card overflow-hidden">
                      <div className="px-5 py-4 border-b border-neutral-100 dark:border-neutral-700 flex items-center justify-between">
                        <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Rules Files Used in Single Analysis</p>
                        <span className="text-xs text-neutral-400">{a.singleRulesList.length} file{a.singleRulesList.length > 1 ? 's' : ''}</span>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-neutral-50 dark:bg-neutral-800/60">
                            <tr>{['#','Rules File','Times Used','Est. Cost','% of Checks'].map(h => (
                              <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">{h}</th>
                            ))}</tr>
                          </thead>
                          <tbody className="divide-y divide-neutral-100 dark:divide-neutral-700/60">
                            {a.singleRulesList.map((rf, i) => (
                              <tr key={rf.name} className="hover:bg-neutral-50 dark:hover:bg-neutral-800/40">
                                <td className="px-4 py-3 text-xs text-neutral-400">{i+1}</td>
                                <td className="px-4 py-3 text-xs text-neutral-700 dark:text-neutral-200 max-w-[240px] truncate font-medium" title={rf.name}>{rf.name}</td>
                                <td className="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-200">{fmt(rf.count)}</td>
                                <td className="px-4 py-3 text-sm font-medium text-emerald-600 dark:text-emerald-400">{fmtUSDShort(rf.cost)}</td>
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-2">
                                    <MiniBar pct={(rf.count / a.singleTotalJobs) * 100} color="bg-teal-500" />
                                    <span className="text-xs text-neutral-500 w-8 text-right">{Math.round((rf.count / a.singleTotalJobs) * 100)}%</span>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}
            </motion.div>
          )}
        </>
      )}

      {!loading && !a && !error && (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <ChartBarIcon className="w-12 h-12 text-neutral-300 dark:text-neutral-700 mb-3" />
          <p className="text-sm font-medium text-neutral-600 dark:text-neutral-300">No data yet</p>
          <p className="text-xs text-neutral-400 mt-1">Metrics will appear here after your first job completes.</p>
        </div>
      )}
    </div>
  );
}